import { z } from 'zod';
// Brand guidelines — the design system Mag reads when she generates
// creative (HTML/CSS templates pull these as CSS variables; the medium
// router uses them to keep stylized fal.ai prompts on-brand).
//
// This is a sibling concept to brand_brain.assets.brand_kit, not a
// replacement. brand_kit lists what assets exist (logo variants,
// palette swatches, fonts available); brand_guidelines describes how
// they should be used — which color is the primary, which font is for
// display vs body, etc. Templates need the semantic roles, not the
// raw inventory.
//
// Populated by either the visual-scrape pass (s155) or manual entry
// in brand settings (s155). The `source` field tracks provenance so
// the founder can see what was inferred vs what they typed.
/** Loose CSS-color string — hex, rgb(), hsl(), oklch(), or a named color.
 *  We don't pre-parse here; the template renderer treats it as a CSS
 *  color verbatim. */
const cssColorSchema = z.string().min(1).max(64);
export const brandColorRolesSchema = z.object({
    /** The brand's signature accent — buttons, highlights, the "this is us" hit. */
    primary: cssColorSchema,
    /** Optional companion to primary — used for secondary actions, secondary fills. */
    secondary: cssColorSchema.optional(),
    /** Spot color for callouts, badges, mag-pulse dots. */
    accent: cssColorSchema.optional(),
    /** Body text color. Defaults to near-black if absent. */
    ink: cssColorSchema.optional(),
    /** Page / card background. Defaults to white if absent. */
    paper: cssColorSchema.optional(),
    /** Phase 4.26 — the dominant DARK SURFACE bg the brand uses (the
     *  hero-section gray on BIKE = #191919, the dark band on stat-grid-
     *  overlay's premium variant, etc.). Distinct from `ink`, which is
     *  the typography color (often a deeper gray used on light surfaces).
     *  Sophisticated brands have two distinct dark tokens: one for text-
     *  on-light, one for full-surface canvas. When unset, templates
     *  render dark surfaces against `ink` (legacy behavior). */
    darkSurface: cssColorSchema.optional(),
});
export const brandTypographySchema = z.object({
    /** Headlines, hero copy. CSS font-family string. */
    display: z.string().min(1).max(200),
    /** Body copy. */
    body: z.string().min(1).max(200),
    /** Monospace — used for eyebrows, metadata. Optional; falls back to
     *  system mono if absent. */
    mono: z.string().min(1).max(200).optional(),
    /** s241 — accent companion to display. Renders the italicized
     *  clauses inside headlines that follow the "sans clause. _italic
     *  serif clause._" motif. When null, accent spans fall back to
     *  the display family rendered italic. Mirrored from
     *  visualSystem.accentDisplay.family on vision-pass completion. */
    accent: z.string().min(1).max(200).optional(),
});
/** Where the guidelines came from. Used by the settings UI to show
 *  "auto-detected" vs "you set this" — founder can override either way. */
export const brandGuidelinesSourceEnum = z.enum(['scraped', 'manual', 'mixed']);
// ─── Brand style choices (Phase 1 designer-agent step) ────────────
//
// The colors/typography slots cover WHAT the brand uses; brandStyle
// covers HOW. Templates pull from these instead of hardcoding visual
// choices like "pill button" or "16px corner radius". When unset
// (null block), templates fall back to their existing defaults — so
// brands that pre-date this schema render exactly as before.
//
// Populated by:
//   - the vision pass (s242+) when it can confidently extract from
//     the brand's reference screenshots / CSS, OR
//   - the founder in settings (always wins over inferred values).
//
// Why every field is optional: even when brandStyle is set, the
// vision pass might confidently extract buttonShape but not
// bulletStyle. The template falls back per-field.
export const brandStyleSchema = z.object({
    /** CTA button shape. Most brands sit in one of three buckets:
     *   - 'pill'    — fully rounded (border-radius: 9999). Friendly,
     *                 marketing-forward (gestionalo, Stripe, Linear).
     *   - 'rounded' — soft 8-16px radius. Professional, editorial.
     *   - 'flat'    — sharp 0-2px radius. Bold, modernist, fintech-cold.
     *  When unset, templates default to 'pill'. */
    buttonShape: z.enum(['pill', 'rounded', 'flat']).optional(),
    /** Card / surface corner radius style. Same 3-bucket logic as
     *  buttonShape — sharp / rounded / pill. Drives the corner radius
     *  on paper cards, stat-grid cards, comparison-grid columns, etc. */
    cardRadius: z.enum(['sharp', 'rounded', 'pill']).optional(),
    /** Bullet glyph style for list-bearing templates (case-study-
     *  overlay bullets, steps-list, comparison-grid points).
     *   - 'dot'   — solid round dot (gestionalo's mint dots, default).
     *   - 'check' — checkmark glyph (✓), brand-accent colored.
     *   - 'arrow' — chevron / arrow glyph (→).
     *   - 'dash'  — em-dash (—). */
    bulletStyle: z.enum(['dot', 'check', 'arrow', 'dash']).optional(),
});
// ─── Component spec (Phase 2 brand-styled component library) ──────
//
// Where brandStyle carries the simple per-field choices (buttonShape,
// cardRadius, bulletStyle), componentSpec carries the RICH per-
// primitive description. Each primitive in @magig/creative reads its
// matching block to produce the brand's actual look.
//
// Sibling to brandStyle (not merged) so the mental model stays:
// brandStyle = simple knobs, componentSpec = rich shape. Tooling
// for one doesn't accidentally mutate the other.
//
// All fields optional even when the block is set; primitives fall
// back to per-field defaults so a partial spec still renders. A
// brand with componentSpec=null renders identically to Phase 1.
//
// Populated by the vision pass (the "easy"/"medium" extraction
// fields per the brand component library design doc (CommitMind) §7) + founder edits
// in settings (always wins).
/** Three-bucket size scale used across the spec for paddings, gaps,
 *  and density. Primitives map each value to a pixel scale. */
const sizeScaleSchema = z.enum(['tight', 'balanced', 'roomy']);
/** Three-bucket font weight bucket. Primitives map to numeric
 *  weights (medium ≈ 500, bold ≈ 700, heavy ≈ 800). */
// 2026-05-30 — full CSS canonical weight scale, 100-900 in 100 steps.
// Original 3-tier (medium/bold/heavy) lost too much resolution at the
// 100-unit boundaries that matter visually (gestionalo's h1 is 600,
// was over-rounding to 'bold' 700 and rendering heavier than the
// site). Every bucket maps to its canonical CSS numeric in the
// primitives' WEIGHT_MAP.
//   thin = 100, extralight = 200, light = 300, regular = 400,
//   medium = 500, semibold = 600, bold = 700, heavy = 800, black = 900
// `heavy` is kept as the canonical token for 800 (predates this
// expansion; not renamed to 'extrabold' to avoid breaking pre-2026-05-30
// stored rows where heavy is already in use).
const fontWeightBucketSchema = z.enum([
    'thin',
    'extralight',
    'light',
    'regular',
    'medium',
    'semibold',
    'bold',
    'heavy',
    'black',
]);
/** Three-bucket shadow strength. */
const shadowBucketSchema = z.enum(['none', 'soft', 'hard']);
/** Palette role reference — primitives resolve this against the
 *  brand's actual colors at render time, so the spec stays semantic. */
const paletteRoleSchema = z.enum([
    'primary',
    'accent',
    'fg',
    'fgSecondary',
    'ink',
    'paper',
    'decorTint',
]);
export const brandButtonSpecSchema = z.object({
    /** Inner padding scale. Maps to (paddingY × fontSize, paddingX ×
     *  fontSize) tuples in BrandButton. Default 'balanced'. */
    paddingX: sizeScaleSchema.optional(),
    /** Font weight bucket. Default 'bold'. */
    weight: fontWeightBucketSchema.optional(),
    /** Optional leading icon inside the pill. 'play-ring' is the
     *  feature-chips-grid playmark; 'arrow-circle' is a circular
     *  arrow; 'dot' is a brand-accent dot before the text; 'none'
     *  omits. Default 'none'. */
    leadingIcon: z.enum(['none', 'play-ring', 'arrow-circle', 'dot']).optional(),
    /** Trailing glyph after the text. Default 'arrow' (→). */
    trailingGlyph: z.enum(['arrow', 'chevron', 'none']).optional(),
    /** Drop-shadow preset. Default 'none' — most brand buttons have
     *  no shadow today; hook-card's "sticker-y" 4px-offset shadow
     *  was a template-imposed look, not a brand convention. */
    shadow: shadowBucketSchema.optional(),
    /** Phase 4.26 — literal border-radius / fontSize ratio captured
     *  from the brand's primary CTA. When set, BrandButton renders
     *  this ratio × fontSize as the corner radius, OVERRIDING the
     *  bucketed shape's re-derived value. Restores precision the
     *  3-bucket pill/rounded/flat enum throws away (BIKE's
     *  ~1.1×fontSize radius bucketed to 'rounded' rendered as
     *  ~0.6×fontSize when re-derived from the bucket). Cap 3.0 since
     *  anything higher is effectively pill. */
    borderRadiusEm: z.number().min(0).max(3).optional(),
});
/** Phase 4.27 — secondary / non-hero CTA spec. Captures the brand's
 *  outline-or-ghost button variant alongside the primary. Brands like
 *  BIKE pair a bold filled primary ("Quiero aprender inglés") with a
 *  thin-bordered ghost secondary ("Conocer más sobre Camp"); rendering
 *  every CTA in the primary voice flattens that hierarchy.
 *
 *  Inherits the same geometry knobs as brandButtonSpecSchema, plus a
 *  `style` enum + `borderWidthEm` ratio that distinguish the visual
 *  treatment:
 *   - 'ghost'   — transparent bg, brand-color text, no border. Most
 *                 minimal; reads as "tertiary action."
 *   - 'outline' — transparent (or near-paper) bg, visible border in
 *                 the brand color, text in the same color. BIKE's case.
 *   - 'tonal'   — low-saturation tinted bg (~12% alpha of primary),
 *                 brand-color text, no border. Material You-ish.
 *
 *  All fields optional; templates that don't opt into the variant
 *  render unchanged. */
export const brandSecondaryButtonSpecSchema = brandButtonSpecSchema.extend({
    /** Visual treatment for the secondary button. Default 'outline' when
     *  the spec is set but `style` is unset — most brand secondaries we
     *  see in the wild are outline-style. */
    style: z.enum(['ghost', 'outline', 'tonal']).optional(),
    /** Border thickness as a fraction of fontSize. Captured literally
     *  from the live secondary CTA — same precision strategy as
     *  brandButtonSpecSchema.borderRadiusEm. Caller renders
     *  `borderWidth = round(fontSize × borderWidthEm)`. Typical range
     *  0.05-0.15 (a 1-3px border at 20px fontSize). Capped at 0.5 so
     *  extreme values can't dominate the button. */
    borderWidthEm: z.number().min(0).max(0.5).optional(),
});
/** Optional accent tile decoration in a corner of a card (gestionalo
 *  renders mint rounded-squares in the top-left of every feature
 *  card). When `card.accentTile` is unset, BrandCard skips rendering
 *  the tile. */
export const brandCardAccentTileSchema = z.object({
    position: z.enum(['top-left', 'top-right']),
    color: paletteRoleSchema,
    shape: z.enum(['rounded-square', 'circle']),
    /** Tile size as a fraction of the canvas shorter edge. ~0.04 is
     *  the gestionalo proportion (40px on a 1080 canvas). */
    sizeRatio: z.number().min(0.01).max(0.2),
});
/** Optional pill badge in a card corner — typically used as a "más
 *  solicitado" / "popular" / "new" marker. */
export const brandCardBadgeSchema = z.object({
    position: z.enum(['top-right']),
    color: paletteRoleSchema,
    shape: z.enum(['pill']),
});
export const brandCardSpecSchema = z.object({
    /** Inner padding scale. Default 'balanced'. */
    padding: sizeScaleSchema.optional(),
    /** Drop-shadow preset. Default 'soft'. */
    shadow: shadowBucketSchema.optional(),
    /** Optional corner decoration. */
    accentTile: brandCardAccentTileSchema.optional(),
    /** Optional pill badge. */
    badge: brandCardBadgeSchema.optional(),
});
export const brandBulletSpecSchema = z.object({
    /** Glyph color reference. Default 'accent' — most brand-bullet
     *  patterns use the accent color so dots/checks pop. */
    color: paletteRoleSchema.optional(),
    /** Glyph size as a fraction of the bullet's text font size.
     *  Default 1.2 (slightly larger than the text). */
    sizeRatio: z.number().min(0.4).max(3).optional(),
    /** Inner spacing between glyph and text + between bullet rows.
     *  Default 'balanced'. */
    spacing: sizeScaleSchema.optional(),
});
export const brandEyebrowSpecSchema = z.object({
    /** Letter casing. 'small-caps' uses CSS font-feature-settings
     *  when the font supports it; falls back to 'upper' otherwise.
     *  Default 'upper'. */
    case: z.enum(['upper', 'small-caps', 'lower']).optional(),
    /** Typography slot to pull family from. Default 'mono'. */
    font: z.enum(['mono', 'serif', 'sans']).optional(),
    /** Color reference. Default 'primary'. */
    color: paletteRoleSchema.optional(),
    /** Letter-spacing scale. Maps to 0.10em / 0.14em / 0.20em.
     *  Default 'balanced' (0.14em). */
    letterSpacing: sizeScaleSchema.optional(),
    /** Font weight bucket. Default 'bold'. */
    weight: fontWeightBucketSchema.optional(),
});
export const brandDividerSpecSchema = z.object({
    /** Visual style. 'rule' is a single line; 'dots' / 'dashes' use
     *  a repeating pattern; 'none' makes BrandDivider render nothing
     *  (templates that always insert a divider get to opt out per-brand). */
    style: z.enum(['rule', 'dots', 'dashes', 'none']).optional(),
    /** Color reference. Default 'fgSecondary'. */
    color: paletteRoleSchema.optional(),
    /** Line thickness in pixels. Default 1. */
    thickness: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
});
export const brandBgDecorSpecSchema = z.object({
    /** Background pattern. 'radial-dots' is the current default the
     *  editorial templates use; 'lines' is a horizontal-rule pattern;
     *  'noise' is a subtle film grain; 'none' renders nothing. */
    pattern: z.enum(['none', 'radial-dots', 'lines', 'noise']).optional(),
    /** Pattern density. Maps to spacing between pattern elements
     *  (low = sparse, high = dense). Default 'medium'. */
    density: z.enum(['low', 'medium', 'high']).optional(),
    /** Pattern color reference. Default 'decorTint'. */
    color: paletteRoleSchema.optional(),
});
// ─── Phase 3 — title / surface / logo / chip / numberStep / stat ────
//
// Phase 3 closes the gap between captured brand intent and what
// templates actually render. Each new sub-block describes a visible
// brand surface that the existing primitives don't cover (or where
// the inline template code didn't read brand spec). All new blocks
// are optional + nullable — brands without them render the existing
// Phase 2 defaults, preserving backwards compatibility.
/** Headline composition motif. Drives BrandTitle. Most modern brands
 *  pair a bold sans first clause with an italic-serif second clause
 *  (gestionalo: "Tu certificado online, _sin moverte de casa._").
 *  Some brands stay single-typeface; this spec captures both. */
export const brandTitleSpecSchema = z.object({
    /** Which markup token maps to the accent rendering.
     *   - 'none'              → plain bold sans, no accent. Strip markers.
     *   - 'italic-accent'     → `_word_` renders in typography.accent (italic serif).
     *   - 'bold-accent'       → `**word**` renders heavier than base.
     *   - 'bold-italic-accent' → `***word***` renders bold + italic serif accent.
     *  Defaults to 'italic-accent' when typography.accent is set on the
     *  brand, else 'none'. */
    emphasisStyle: z.enum(['none', 'italic-accent', 'bold-accent', 'bold-italic-accent']).optional(),
    /** Where the accent clause typically lands in headlines. Lets the
     *  fill agent put the marker in the brand-typical position when it
     *  generates copy. 'mixed' = brand uses both positions
     *  interchangeably. */
    emphasisPosition: z.enum(['first', 'second', 'mixed']).optional(),
    /** Does the brand always close headlines with `.` ? Gestionalo
     *  does — even short phrases. Many brands skip the period. When
     *  true, BrandTitle appends one if the fill content omits it. */
    trailingPeriod: z.boolean().optional(),
    /** Base font weight for the non-emphasized text. Default 'bold'. */
    baseWeight: fontWeightBucketSchema.optional(),
});
/** Full-canvas surface treatment — the brand's signature background
 *  on a given surface mode. Captures gradients (gestionalo's dark
 *  sections are vertical navy-to-deep-navy gradients), tinted image
 *  overlays, and flat colors in one structured shape. Drives
 *  BrandSurface; replaces inline `background: surface.bg` on every
 *  template's root container. */
/** Narrower palette-role enum for gradient stops. Surfaces are
 *  background colors — using vibrant action colors (primary/accent)
 *  as stops produces stark "bullseye" radials and ugly hero gradients
 *  (the gestionalo regression: mint primary at 0.4 + navy ink at 1.0
 *  rendered a mint-to-navy bullseye instead of the brand's subtle
 *  navy vignette). Restricting to background-tier roles forces vision
 *  to default to flat when the only "gradient" it sees has primary in
 *  it — a missed subtle gradient renders less wrong than a fabricated
 *  garish one.
 *
 *  s216-54h.109 — `secondary` added: it's the brand's muted companion
 *  navy (NOT an action color), the one mid-tone the original four roles
 *  lacked. Without it a dark brand's "subtle navy vignette" collapses to
 *  solid ink (ink/decorTint/fgSecondary all resolve to ink), which is
 *  exactly what flattened gestionalo's hero glow. It's the lighter stop
 *  the vignette needs (gestionalo: secondary #1f3f66 → ink #0f2a44) and
 *  it's still background-tier, so it doesn't reintroduce the bullseye. */
const surfaceGradientRoleSchema = z.enum(['ink', 'paper', 'fgSecondary', 'decorTint', 'secondary']);
export const brandSurfaceGradientStopSchema = z.object({
    color: surfaceGradientRoleSchema,
    /** Position along the gradient axis, 0..1. */
    position: z.number().min(0).max(1),
});
export const brandSurfaceSpecSchema = z.object({
    /** Surface composition.
     *   - 'flat'         → solid color (current behavior; default).
     *   - 'gradient'     → linear / radial gradient using palette roles.
     *   - 'image-tinted' → photographic bg with a brand-color overlay.
     *  When unset, BrandSurface falls back to flat. */
    kind: z.enum(['flat', 'gradient', 'image-tinted']).optional(),
    gradient: z
        .object({
        direction: z.enum(['top-to-bottom', 'top-left-to-bottom-right', 'radial']),
        stops: z.array(brandSurfaceGradientStopSchema).min(2).max(4),
    })
        .optional(),
    /** When kind='image-tinted', the brand-color overlay role. */
    tintColor: paletteRoleSchema.optional(),
    /** Overlay opacity, 0..1. Default 0.45 when kind='image-tinted'. */
    tintOpacity: z.number().min(0).max(1).optional(),
});
/** Logo lockup treatment. Most brands frame their icon in a colored
 *  square (gestionalo: mint-bg + mint check) and pair it with a
 *  wordmark. This spec captures the frame styling so BrandLogo can
 *  render the lockup consistently across templates instead of every
 *  template hand-rolling a BrandMark helper. */
export const brandLogoSpecSchema = z.object({
    /** Color role behind the icon mark. 'none' = no frame, just icon. */
    markBg: z.union([paletteRoleSchema, z.literal('none')]).optional(),
    /** Frame shape. */
    markShape: z.enum(['sharp', 'rounded', 'circle']).optional(),
    /** Padding inside the frame around the icon. */
    markPadding: sizeScaleSchema.optional(),
    /** Whether the brand renders a wordmark alongside the icon. */
    wordmark: z.enum(['with', 'without']).optional(),
    /** Which typography slot the wordmark uses. */
    wordmarkFont: z.enum(['display', 'body', 'mono']).optional(),
    /** Wordmark weight. */
    wordmarkWeight: fontWeightBucketSchema.optional(),
});
/** Floating validation chip (gestionalo's "Validado por profesional /
 *  Listo para presentar" pills that hover over device mockups).
 *  Schema only in Phase 3.0; primitive ships when a template needs it. */
export const brandChipSpecSchema = z.object({
    bg: paletteRoleSchema.optional(),
    fg: paletteRoleSchema.optional(),
    shape: z.enum(['pill', 'rounded', 'sharp']).optional(),
    hasLeadingIcon: z.boolean().optional(),
    iconColor: paletteRoleSchema.optional(),
});
/** Numbered step indicator (gestionalo's "01 / 02 / 03 / 04" circles
 *  in the how-it-works section). Schema only in Phase 3.0. */
export const brandNumberStepSpecSchema = z.object({
    shape: z.enum(['circle', 'square', 'pill']).optional(),
    bg: z.union([paletteRoleSchema, z.literal('none')]).optional(),
    borderColor: paletteRoleSchema.optional(),
    numeralFont: z.enum(['display', 'body', 'mono']).optional(),
    numeralColor: paletteRoleSchema.optional(),
});
/** Big-number stat treatment (the "27h promedio" / "$3.080" displays).
 *  Schema only in Phase 3.0. */
export const brandStatSpecSchema = z.object({
    numeralFont: z.enum(['display', 'body', 'mono']).optional(),
    numeralWeight: fontWeightBucketSchema.optional(),
    numeralColor: paletteRoleSchema.optional(),
    labelPosition: z.enum(['below', 'above', 'inline']).optional(),
});
export const brandComponentSpecSchema = z.object({
    /** Brand-styled button primitive. Extends brandStyle.buttonShape. */
    button: brandButtonSpecSchema.optional(),
    /** Phase 4.27 — secondary / non-hero CTA spec. Optional, additive;
     *  brands without an outline-style variant render every CTA as the
     *  primary. Templates opt in via BrandButton's `variant` prop. */
    secondaryButton: brandSecondaryButtonSpecSchema.optional(),
    /** Brand-styled card / surface primitive. Extends brandStyle.cardRadius. */
    card: brandCardSpecSchema.optional(),
    /** Brand-styled bullet glyph. Extends brandStyle.bulletStyle. */
    bullet: brandBulletSpecSchema.optional(),
    /** Brand-styled eyebrow / pillar label. New in Phase 2. */
    eyebrow: brandEyebrowSpecSchema.optional(),
    /** Brand-styled divider rule. New in Phase 2. */
    divider: brandDividerSpecSchema.optional(),
    /** Brand-styled background decoration motif. Replaces the inline
     *  radial-dot pattern every editorial template hardcodes today. */
    bgDecor: brandBgDecorSpecSchema.optional(),
    /** Phase 3 — headline composition motif (bold sans + italic-accent
     *  clause). Drives BrandTitle. */
    title: brandTitleSpecSchema.optional(),
    /** Phase 3 — full-canvas surface treatment (gradient, image, flat).
     *  Drives BrandSurface. */
    surface: brandSurfaceSpecSchema.optional(),
    /** Phase 3 — logo lockup styling (framed icon + wordmark). Drives
     *  BrandLogo. */
    logo: brandLogoSpecSchema.optional(),
    /** Phase 3 — floating validation chip styling. Schema only; primitive
     *  ships when a template uses it. */
    chip: brandChipSpecSchema.optional(),
    /** Phase 3 — numbered step indicator styling. Schema only. */
    numberStep: brandNumberStepSpecSchema.optional(),
    /** Phase 3 — big-number stat styling. Schema only. */
    stat: brandStatSpecSchema.optional(),
    /** Global density scale — affects outer canvas padding, vertical
     *  gaps between primitives, breathing room. Default 'balanced'.
     *  When the brand has a notably tight or roomy site, this single
     *  knob shifts all templates without per-template tuning. */
    density: sizeScaleSchema.optional(),
    /** Phase 4.31a — anti-patterns: short prose rules of what NOT to do
     *  on this brand. Borrowed conceptually from open-design's
     *  DESIGN.md anti-patterns section. Surfaced into the template-fill
     *  prompt as "avoid these" guidance so the agent doesn't drift into
     *  voice/visual moves the brand explicitly rejects.
     *
     *  Examples a vision pass might emit:
     *   - "never render filled CTAs in primary on paper; use the ghost
     *     outline variant instead"
     *   - "avoid emoji in headlines — the brand uses none"
     *   - "no all-caps body copy; sentence case only"
     *   - "no centered headlines; left-aligned only"
     *
     *  Capped at 8 entries to keep the prompt bounded. Optional —
     *  brands without explicit anti-patterns just don't get the block. */
    antiPatterns: z.array(z.string().min(1).max(200)).max(8).optional(),
});
// ─── Visual system (vision-pass output) ─────────────────────────────
//
// The colors + typography slots above carry literal values for the
// renderer to splice into templates. They don't carry the *meaning*
// of how the brand uses them — that's what visualSystem is for.
// Generated by the multimodal vision agent reading screenshots of
// the brand's marketing site. Lets the creative-fill agent + template
// router condition on actual brand convention (sans+serif italic
// pairing, dark↔light surface alternation, period-stopped voice
// pattern, …) instead of just a primary hex.
export const surfaceModeSchema = z.enum([
    'full-bleed-light',
    'full-bleed-dark',
    'card-on-light',
    'card-on-dark',
    'gradient',
    'image-backed',
]);
export const visualSystemPaletteEntrySchema = z.object({
    /** The color, in whatever CSS form the brand uses. */
    value: z.string().min(1).max(64),
    /** What this color does in the brand's system, in voice.
     *  "navy canvas — dominant on-brand surface for dark scenes",
     *  "emerald cta + chip accent, signals action",
     *  "cream wash — alt canvas, softens the dark scenes". */
    role: z.string().min(1).max(200),
});
export const typographyRoleSchema = z.object({
    /** CSS font-family the brand uses for this role. */
    family: z.string().min(1).max(200),
    /** How the brand uses this typeface: case (sentence/upper),
     *  weight, optical sizing, italic vs upright, and *when* it
     *  shows up. "sentence-case sans, weight 700+, anchors every
     *  primary headline". */
    usage: z.string().min(1).max(280),
});
export const visualSystemSchema = z.object({
    /** 3-8 colors in the order the vision agent considers them
     *  important. Includes a role description per color so the
     *  creative-fill agent can pick "the cta color" not "the third
     *  hex". */
    palette: z.array(visualSystemPaletteEntrySchema).max(8).default([]),
    /** Dominant display family — the one that anchors the most
     *  headlines. Null when the brand uses only one typeface end-to-end. */
    primaryDisplay: typographyRoleSchema.nullable(),
    /** Counter-display used as accent (italic serif beside a sans head,
     *  hand-lettered ribbon beside a clean grotesque). Drives the
     *  Instrument-Serif-pairs-with-Plus-Jakarta-Sans gestalt that pure
     *  CSS reads can't see. Null when the brand uses a single typeface. */
    accentDisplay: typographyRoleSchema.nullable(),
    /** Body family. Often the same as primaryDisplay's family. */
    body: typographyRoleSchema.nullable(),
    /** Which surfaces the brand actually uses, in approximate
     *  prevalence order. Lets templates pick a canvas the brand
     *  has already shown they're comfortable with. */
    surfaceModes: z.array(surfaceModeSchema).max(6).default([]),
    /** Short prose patterns the brand uses repeatedly — shapes,
     *  treatments, structural conventions. 3-8 entries, each one
     *  a single descriptive sentence in mag voice.
     *  "card-on-dark with neon-green badge",
     *  "italic serif clause inset inside a sans headline, period-stopped",
     *  "numbered wizard with green checkmark progress". */
    motifs: z.array(z.string().min(1).max(240)).max(10).default([]),
    /** Voice/visual conventions — how the brand uses copy + type
     *  together. 2-6 entries. "italic-serif accent functions as poetic
     *  emphasis after a sans declarative", "punctuates declarative
     *  fragments with a single period for confident finality". */
    voiceConventions: z.array(z.string().min(1).max(240)).max(8).default([]),
    /** Icon treatment description — "thin green outline icons on
     *  rounded-square chips, paired with check marks". Null when the
     *  brand doesn't lean on iconography. */
    iconography: z.string().max(280).nullable(),
    /** Layout density posture — minimal (lots of breathing room),
     *  balanced, dense (info-rich). Mirrors the creative brief's
     *  density axis so the template router can match. */
    density: z.enum(['minimal', 'balanced', 'dense']).nullable(),
    /** When the vision pass ran. Lets the UI surface "auto-read X days
     *  ago". Null if no vision pass has succeeded yet. */
    inferredAt: z.string().datetime().nullable(),
});
/** Screenshots of the brand's marketing site, captured during the
 *  Playwright pass + uploaded to R2. Persisted with the brand so the
 *  vision pass can re-run without re-fetching, and so the creative-fill
 *  agent can pull one as a reference image when drafting. */
export const referenceScreenshotSchema = z.object({
    url: z.string().url(),
    /** 'full-page' is one continuous capture of the whole document,
     *  added so the onboarding browser window can pan over real scroll
     *  instead of stacking the four semantic frames edge-to-edge and
     *  implying a continuity they never had (they are viewport samples
     *  at 0 / 1.1x / 3.2x / probed-dark, so stacking them repeated the
     *  site's sticky nav and skipped ~1.1 viewports of page). The four
     *  semantic kinds still exist and are still what the vision pass
     *  reads — full-page is for display only. */
    kind: z.enum(['hero', 'mid-page', 'dark-section', 'deep-page', 'full-page']),
    scrollY: z.number().min(0),
    bodyBackground: z.string().nullable(),
    capturedAt: z.string().datetime(),
});
export const brandGuidelinesSchema = z.object({
    colors: brandColorRolesSchema,
    typography: brandTypographySchema,
    /** Primary logo URL (light backgrounds). R2-hosted, served via the
     *  assets CDN. Null when the brand hasn't supplied or scraped a logo
     *  yet — templates fall back to the wordmark. */
    logoUrl: z.string().url().nullable(),
    /** Alt logo for dark backgrounds (inverted / white-on-dark). Falls
     *  back to logoUrl when null. */
    logoDarkUrl: z.string().url().nullable(),
    /** Phase 4.28a — R2-hosted favicon. Distinct from logoUrl: the
     *  favicon is the brand's distilled mark that already fits inside a
     *  small circle (BIKE's bike-silhouette glyph), whereas logoUrl is
     *  the full wordmark/lockup. Always scraped when a `<link rel="icon">`
     *  declaration exists or `/favicon.ico` resolves. Used as the
     *  default avatar for the BrandSwitcher pill in the top nav. */
    faviconUrl: z.string().url().nullable().optional(),
    /** Brand-specific design motifs to weave into templates. Free-text
     *  list — Mag interprets them ("sparkle" → renders a ✦ accent,
     *  "thick rules" → uses 2px borders instead of 1px). */
    visualMotifs: z.array(z.string().min(1).max(80)).default([]),
    /** A handful of representative copy samples (taglines, hero lines)
     *  that templates can lean on for placeholder text. Echo of what
     *  lives in brand_brain.voice, surfaced here for the renderer. */
    voiceSamples: z.array(z.string().min(1).max(280)).default([]),
    /** Free-text notes on illustration / photography style — "flat,
     *  pastel", "muted documentary photography", etc. The medium router
     *  threads this into fal.ai prompts. */
    illustrationStyle: z.string().max(500).nullable(),
    /** Provenance — see comment on the enum. */
    source: brandGuidelinesSourceEnum,
    /** When the most recent auto-scrape ran. Null if never scraped. */
    scrapedAt: z.string().datetime().nullable(),
    /** The URL the scraper read. Null if never scraped. */
    scrapedUrl: z.string().url().nullable(),
    /** Rich vision-pass output — palette with roles, typography pairing,
     *  motifs, voice conventions, etc. Null until the vision pass runs
     *  successfully. Founders can edit individual fields in settings. */
    visualSystem: visualSystemSchema.nullable().default(null),
    /** R2-hosted screenshots from the most recent Playwright pass.
     *  Empty array when chromium was never run or all captures failed. */
    referenceScreenshots: z.array(referenceScreenshotSchema).max(8).default([]),
    /** s242 — brand-derived style choices (button shape, card radius,
     *  bullet style). Templates pull from this instead of hardcoding
     *  visual chrome. Null means "use template defaults" — backwards
     *  compatible with every existing brand row. */
    brandStyle: brandStyleSchema.nullable().default(null),
    /** s243 (Phase 2) — rich per-primitive component spec. Sibling to
     *  brandStyle: brandStyle is the simple knobs, componentSpec is
     *  the rich shape. Null means "use template defaults" — backwards
     *  compatible with every existing brand row, identical to Phase 1
     *  behavior. See the brand component library design doc (CommitMind) for the full
     *  schema + extraction strategy. */
    componentSpec: brandComponentSpecSchema.nullable().default(null),
});
/** A sensible empty default — used when a brand has no guidelines row
 *  yet so the templates have *something* to render against. The
 *  defaults intentionally use system fonts and brand-neutral colors so
 *  it's obvious nothing's been customized yet. */
export const DEFAULT_BRAND_GUIDELINES = {
    colors: {
        primary: '#0F0E17',
        ink: '#0F0E17',
        paper: '#F4EFE6',
    },
    typography: {
        display: 'ui-sans-serif, system-ui, sans-serif',
        body: 'ui-sans-serif, system-ui, sans-serif',
    },
    logoUrl: null,
    logoDarkUrl: null,
    visualMotifs: [],
    voiceSamples: [],
    illustrationStyle: null,
    source: 'manual',
    scrapedAt: null,
    scrapedUrl: null,
    visualSystem: null,
    referenceScreenshots: [],
    brandStyle: null,
    componentSpec: null,
};
//# sourceMappingURL=brand-guidelines.js.map